/*
 * @Author: Ryan
 * @Date: 2025-12-03
 * @Description: Footer component with responsive layout
 */
import Image from 'next/image';
import Link from 'next/link';
import React from 'react';

import { LANDING_PAGES } from '@/landing/video-translator/config';
import {
  aiRemovalURL,
  aiTiktokVideoURL,
  aiVideoEditURL,
  avatarVideoProURL,
  faceSwapperURL,
  imageEnhanceURL,
  imageGenerateURL,
  imageTranslationURL,
  PolicyURL,
  pricingURL,
  productAvatarURL,
  TermsURL,
  videoGenerateURL,
  videoTranslateURL,
  voiceCloneURL,
} from '@/utils/routeConstants';

interface FooterLink {
  text: string;
  href: string;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

// 将 slug 转换为首字母大写的显示名称
const formatSlugToTitle = (slug: string): string => {
  return slug
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// 从 LANDING_PAGES 生成 Solutions 链接
const generateSolutionsLinks = (): FooterLink[] => {
  return LANDING_PAGES.map((page) => ({
    text: formatSlugToTitle(page.slug),
    href: `/tools/video-translator/${page.slug}`,
  }));
};

const Footer: React.FC = () => {
  const sections: FooterSection[] = [
    {
      title: 'Product',
      links: [
        { text: 'AI Video Generator', href: videoGenerateURL },
        { text: 'AI Image Generator', href: imageGenerateURL },
        { text: 'AI Video Editor', href: aiVideoEditURL },
        { text: 'Video Translation', href: videoTranslateURL },
        { text: 'Image Translation', href: imageTranslationURL },
        { text: 'AI Avatar Generator', href: avatarVideoProURL },
        { text: 'Product Avatar', href: productAvatarURL },
        { text: 'Face Swap', href: faceSwapperURL },
        { text: 'AI Removal', href: aiRemovalURL },
        { text: 'Voice Clone', href: voiceCloneURL },
        { text: 'Image Enhance', href: imageEnhanceURL },
      ],
    },
    {
      title: 'Solutions',
      links: generateSolutionsLinks(),
    },
    {
      title: 'Resources',
      links: [
        { text: 'AI TikTok Video Generator', href: aiTiktokVideoURL },
        { text: 'AI UGC Video Generator', href: '/ai-ugc-video-generator' },
      ],
    },
    {
      title: 'Company',
      links: [
        { text: 'Pricing', href: pricingURL },
        { text: 'Terms of Service', href: TermsURL },
        { text: 'Privacy Policy', href: PolicyURL },
      ],
    },
  ];

  return (
    <footer className="w-full bg-[#0A0A0A] text-white py-12 pad:py-16 px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* 上半部分：链接分栏 */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 pad:gap-12 pb-10 border-b border-gray-800">
          {sections.map((section) => (
            <div key={section.title} className="flex flex-col gap-4">
              <h3 className="text-base font-semibold text-white">{section.title}</h3>
              <ul className="flex flex-col gap-3">
                {section.links.map((link) => (
                  <li key={link.text}>
                    <Link
                      href={link.href}
                      className="text-sm text-gray-400 hover:text-white transition-colors"
                    >
                      {link.text}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* 下半部分：Logo、社交图标、描述、版权 */}
        <div className="pt-8 flex flex-col items-center gap-4">
          {/* Logo 和社交图标行 */}
          <div className="flex items-center gap-4">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <Image
                src="/images/footer_logo.png"
                alt="FalcoCut"
                width={29}
                height={29}
                className="object-contain"
              />
              <span className="text-lg font-semibold text-white">FalcoCut</span>
            </div>

            {/* 社交图标 */}
            <div className="flex items-center gap-3">
              {/* YouTube */}
              <a
                href="https://www.youtube.com/@FalcoCut"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="YouTube"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                </svg>
              </a>

              {/* Instagram */}
              <a
                href="https://www.instagram.com/falcocutai_official"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Instagram"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
                </svg>
              </a>

              {/* TikTok */}
              <a
                href="https://www.tiktok.com/@falcocutai"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="TikTok"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                </svg>
              </a>

              {/* X (Twitter) */}
              <a
                href="https://x.com/falcocut_ai"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="X"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
            </div>
          </div>

          {/* 描述文本 */}
          <p className="text-sm text-gray-500">
            Get videos for markets around the world.
          </p>

          {/* 版权信息 */}
          <p className="text-sm text-gray-500">© 2026 FalcoCut</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
